import React, { useState } from "react";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import PortalAuthShell, { authCard, authLabel, authInput, authButton } from "./PortalAuthShell";

/**
 * "Forgot password" — asks the backend to mail a reset link.
 *
 * The backend answers identically whether or not the address has an account (no
 * account enumeration), so this screen shows the same confirmation either way. It
 * must never imply the address was or wasn't found.
 */
export default function PortalForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/forgot-password", { email: email.trim() });
    } catch {
      /* Deliberately ignored: a failure here would reveal whether the account exists. */
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <PortalAuthShell title="Check your email" subtitle="If that address has a portal account, a reset link is on its way.">
        <div className={authCard} data-testid="portal-forgot-sent">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
            <div className="text-sm text-slate-600">
              <p>We've sent a link to <span className="font-semibold text-slate-900">{email.trim()}</span> if an account exists for it.</p>
              <p className="mt-2">The link is valid for <b>2 hours</b> and can be used once. Check your spam folder if it hasn't arrived in a few minutes.</p>
            </div>
          </div>
          <button type="button" onClick={() => { setSent(false); }} className="text-[13px] text-blue-700 hover:underline">
            Use a different email address
          </button>
        </div>
      </PortalAuthShell>
    );
  }

  return (
    <PortalAuthShell title="Forgot your password?" subtitle="We'll email you a link to set a new one.">
      <form onSubmit={submit} className={authCard}>
        <div>
          <label className={authLabel}>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            data-testid="portal-forgot-email" className={authInput} placeholder="you@company.com" />
        </div>
        <button type="submit" disabled={busy} data-testid="portal-forgot-btn" className={authButton}>
          <PaperPlaneTilt size={16} /> {busy ? "Sending…" : "Send reset link"}
        </button>
      </form>
    </PortalAuthShell>
  );
}
